/**
 * Created by Ning Jiang on 11/23/2016.
 */

import {IGameConfig} from "@nolimitcity/slot-game/bin/core/gameconfig/IGameConfig";
import {IReelSets} from "@nolimitcity/slot-game/bin/core/reel/reelset/IReelSets";
import {LayerScaleOption} from "@nolimitcity/slot-game/bin/core/stage/LayerScaleOption";
import {IDeviceDependentConfig} from "@nolimitcity/slot-game/bin/core/useragent/UserAgent";
import {IWinRatios, WinRatioType} from "@nolimitcity/slot-game/bin/core/winpresentation/WinRatio";
import {IGameTweakerConfig} from "@nolimitcity/slot-game/bin/dev/tweaker/GameTweaker";
import {Back, Ease} from "gsap";
import {ImmortalFruitsGameAssets} from "./ImmortalFruitsGameAssets";
import {IImmortalFruitsLayerConfig} from "./stage/IImmortalFruitsLayerConfig";
import {IImmortalFruitsSymbolStates} from "./symbols/ImmortalFruitsReelSymbol";
import {SpinMode} from "@nolimitcity/slot-game/bin/core/gametype/SpinMode";
import {BetWinMode} from "@nolimitcity/slot-game/bin/core/gametype/BetWinMode";
import {ImmortalFruitsSoundConfig} from "./ImmortalFruitsSoundConfig";
import {ImmortalFruitsLayerConfig} from "./config/IImmortalFruitsLayerConfig";
import {ISymbolStateConfig} from "@nolimitcity/slot-game/bin/core/reelsymbol/StateReelSymbol";
import {SoundManager} from "./resource/sound/SoundManager";

export interface IImmortalFruitsWinRatioConfig {
    readonly ratio:number;
    readonly text?:string;
    readonly duration:number; // In second.
    readonly type?:WinRatioType;
    readonly sound:string;
}

export interface IImmortalFruitsWinRatios extends IWinRatios{
    readonly normalWin:IImmortalFruitsWinRatioConfig[];
    readonly bigWin:IImmortalFruitsWinRatioConfig[];
}
export class ImmortalFruitsGameConfig implements IGameConfig {

    public static instance:ImmortalFruitsGameConfig = new ImmortalFruitsGameConfig();

    BROWSER_FPS:number = 30;

    GAME_WIDTH:number = 720;
    GAME_HEIGHT:number = 720;

    DEFAULT_LAYER_SCALE_OPTION:IDeviceDependentConfig<LayerScaleOption> = {
        desktop : {landscape : LayerScaleOption.FIT_WINDOW},
        mobile : {
            landscape : LayerScaleOption.FIT_WINDOW,
            portrait : LayerScaleOption.REELS_FIT_WINDOW
        }
    };
    LAYERS: IImmortalFruitsLayerConfig = ImmortalFruitsLayerConfig.getConfig();

    BET_WIN_MODE:BetWinMode = BetWinMode.BET_LINE;
    SPIN_MODE:SpinMode = SpinMode.SPIN;

    REEL_AREA_POS_X:number = 29;
    REEL_AREA_POS_Y:number = 128;
    REEL_AREA_WIDTH:number = 730;
    REEL_AREA_HEIGHT:number = 556;
    PORTRAIT_OFFSET:number = 48;
    REEL_AREA_CENTER_X: number = this.REEL_AREA_POS_X + this.REEL_AREA_WIDTH * 0.5;
    REEL_AREA_CENTER_Y: number = this.REEL_AREA_POS_Y + this.REEL_AREA_HEIGHT * 0.5;
    REEL_AREA_MASK:boolean = true;

    SYMBOL_STATES:IImmortalFruitsSymbolStates = {
        normal : {
            prio : 0,
            keyword : "normal",
            texturesCreator : (symName:string,stateKey:string) => {
                return ImmortalFruitsGameAssets.getSymbolStateTextures(symName,stateKey);
            }
        },
        spin : {
            prio : 1,
            keyword : "spin",
            texturesCreator : (symName:string, stateKey:string) => {
                return ImmortalFruitsGameAssets.getSymbolStateTextures(symName, stateKey);
            }
        },
        noWin : {
            prio : 2,
            keyword : "noWin",
            texturesCreator : (symName:string) => {
                return ImmortalFruitsGameAssets.getSymbolStateTextures(symName);
            }
        },
        win : {
            prio : 3,
            keyword : "win",
            texturesCreator : (symName:string) => {
                if (symName === "W") { // temp before we have W_win
                    return ImmortalFruitsGameAssets.getSymbolWinStateTexture(symName);
                } else {
                    return ImmortalFruitsGameAssets.getSymbolStateTextures(symName);
                }
            }
        },
        land : {
            prio : 5,
            keyword : "Land",
            symbols: ["S","W"],
            texturesCreator : (symName:string,statekey:string) => {
                if (symName === "W") {
                    return ImmortalFruitsGameAssets.getSymbolLandingStateTexture(symName,statekey);
                }else{
                    return ImmortalFruitsGameAssets.getSymbolStateTextures(symName);
                }
            }
        },
        idle: {
            prio: 4,
             keyword: "Idle",
            symbols: ["S", "W"],
            texturesCreator: (symName: string,stateKey:string) => {
                if (symName === "W") {
                    return ImmortalFruitsGameAssets.getSymbolWinStateTexture(symName);
                }else{
                    return ImmortalFruitsGameAssets.getSymbolStateTextures(symName);
                }
            }
        },
        hidden : {
            prio : 4,
            keyword : "hidden"
        },
    };
    SYMBOL_WIDTH:number = 130;
    SYMBOL_HEIGHT:number = 139;
    SYMBOL_BOTTOM_PADDING:number = 0;

    REELS_NUM:number = 5;
    SPACE_BETWEEN_REELS:number = 3;
    OFFSET:number = 0;
    OFFSET_Y:number = 0;
    REEL_DISPLAY_POSITIONS:[number, number][] = [
        [this.OFFSET+this.SYMBOL_WIDTH * 0.5, this.OFFSET_Y+0],
        [this.OFFSET+this.SYMBOL_WIDTH * 1.5 + this.SPACE_BETWEEN_REELS, this.OFFSET_Y+ 0+this.SYMBOL_BOTTOM_PADDING],
        [this.OFFSET+this.SYMBOL_WIDTH * 2.5 + this.SPACE_BETWEEN_REELS * 2, this.OFFSET_Y+ +this.SYMBOL_BOTTOM_PADDING*2],
        [this.OFFSET+this.SYMBOL_WIDTH * 3.5 + this.SPACE_BETWEEN_REELS * 3,  this.OFFSET_Y+this.SYMBOL_BOTTOM_PADDING*3],
        [this.OFFSET+this.SYMBOL_WIDTH * 4.5 + this.SPACE_BETWEEN_REELS * 4, this.OFFSET_Y+ this.SYMBOL_BOTTOM_PADDING*4],
        [this.OFFSET+this.SYMBOL_WIDTH * 5.5 + this.SPACE_BETWEEN_REELS * 5, this.OFFSET_Y+0]];
    SYMBOLS_NUM_IN_REEL:[number, number, number, number, number] = [1, 1, 4, 1, 1];


    REEL_SPIN_TIME:number = 0.9;
    REEL_FAST_SPIN_SPIN_TIME:number = 0.2;

    REEL_SPIN_START_SYMBOL_CHANGE_TO_SPIN_STATE_DURATION:number = 0.2;
    REEL_SPIN_START_SYMBOL_CHANGE_TO_SPIN_STATE_DELAY:number = 0.3;
    REEL_SPIN_START_EASE:Ease = Back.easeIn.config(4);
    REEL_SPIN_STOP_SYMBOL_CHANGE_FROM_SPIN_STATE_DURATION:number = 0.2;
    REEL_SPIN_STOP_SYMBOL_CHANGE_FROM_SPIN_STATE_DELAY:number = 0;
    REEL_SPIN_STOP_ELASTIC_CONFIG:[number, number] = [1.5, 0.95];
    REEL_SPIN_QUICK_STOP_ELASTIC_CONFIG:[number, number] = [1.2, 0.75];

    REEL_SPIN_START_SPEED:number = 0.35;
    REEL_SPIN_SPIN_SPEED:number = 0.05;
    REEL_NEAR_WIN_SPIN_SPEED:number = 0.03;
    REEL_SPIN_STOP_SPEED:number = 0.5;
    REEL_SPIN_QUICK_STOP_SPEED:number = 0.3;

    REEL_STOP_DELAY:number = 0.3;
    REEL_QUICK_STOP_DELAY:number = 0.05;
    REEL_NEAR_WIN_STOP_DELAY:number = 0.0; //This needs to be set to 0 if you want to control this in the gameroundmanager config.

    REEL_SPIN_STOP_SYMBOL_STATE:() => ISymbolStateConfig = () => {
        return ImmortalFruitsGameConfig.instance.SYMBOL_STATES.land;
    };
    REEL_SETS:IReelSets = {
        "BASE_REELSET" : [
            ["M1","M1","M1","M1","S","L1","L1","L1","L1", "L3","L3","L3","L3", "M2", "M2","M2","M2","L2","L2","L2","L2", "L5", "L5", "L5", "L5", "M3", "M3","M3","M3","L4","L4","L4","L4","S"],
            ["M1","M1","M1","M1", "L1","L1","L1","L1", "L3","L3","L3","L3", "M2", "M2","M2","M2","L2","L2","L2","L2", "L5", "L5", "L5", "L5","M3", "M3","M3","M3","W", "W","W","W","L4","L4","L4","L4"],
            ["M1","M1","M1","M1", "L1","L1","L1","L1", "L3","L3","L3","L3", "M2", "M2","M2","M2","L2","L2","L2","L2", "S", "L5", "L5", "L5", "M3", "M3","M3","M3","L5","W", "W","W","W","L4","L4","L4","L4"],
            ["M1","M1","M1","M1", "L1","L1","L1","L1", "L3","L3","L3","L3", "M2", "M2","M2","M2","L2","L2","L2","L2", "L5", "L5", "L5", "L5","M3", "M3","M3","M3", "W", "W","W","W","L4","L4","L4","L4"],
            ["M1","M1","M1","M1", "L1","L1","L1","L1", "L3","L3","L3","L3", "M2", "M2","M2","M2","L2","L2","L2","L2", "L5", "L5", "L5", "L5", "M3", "M3","M3","M3","L4","L4","L4","L4", "S"],
        ],
        "BONUS_REELSET": [
            ["M1","M1","M1","M1", "L1","L1","L1","L1", "L3","L3","L3","L3", "M2", "M2","M2","M2","L2","L2","L2","L2", "M3", "M3","M3","M3", "L5", "L5", "L5", "L5","L4","L4","L4","L4", "S"],
            ["M1","M1","M1","M1", "L1","L1","L1","L1", "L3","L3","L3","L3", "M2", "M2","M2","M2","L2","L2","L2","L2", "M3", "M3","M3","M3", "L5", "L5", "L5", "L5", "W", "W","W","W","L4","L4","L4","L4"],
            ["M1","M1","M1","M1", "L1","L1","L1","L1", "L3","L3","L3","L3", "M2", "M2","M2","M2","L2","L2","L2","L2", "M3", "M3","M3","M3","S", "L5", "L5", "L5", "L5","W", "W","W","W","L4","L4","L4","L4"],
            ["M1","M1","M1","M1", "L1","L1","L1","L1", "L3","L3","L3","L3", "M2", "M2","M2","M2","L2","L2","L2","L2", "M3", "M3","M3","M3", "L5", "L5", "L5", "L5","W", "W","W","W","L4","L4","L4","L4"],
            ["M1","M1","M1","M1", "L1","L1","L1","L1", "L3","L3","L3","L3", "M2", "M2","M2","M2","L2","L2","L2","L2", "M3", "M3","M3","M3", "L5", "L5", "L5", "L5","L4","L4","L4","L4","S"],

        ]
    };

    WIN_RATIOS:IImmortalFruitsWinRatios = {
        normalWin : [
            {
                ratio : 0,
                text : "Tiny Win",
                duration : 0,
                type : WinRatioType.TINY,
                sound: ImmortalFruitsSoundConfig.WIN_TINY
            },
            {
                ratio : 1.5,
                text : "Small Win",
                duration : 0,
                type : WinRatioType.SMALL,
                sound: ImmortalFruitsSoundConfig.WIN_SMALL
            },
            {
                ratio : 3,
                text : "Medium Win",
                duration : 0.4,
                type : WinRatioType.MEDIUM,
                sound: ImmortalFruitsSoundConfig.WIN_MEDIUM
            },
            {
                ratio : 5,
                text : "Large Win",
                duration : 0.8,
                type : WinRatioType.LARGE,
                sound: ImmortalFruitsSoundConfig.WIN_LARGE
            }
        ],
        bigWin : [
            {
                ratio : 15,
                text : "Big Win",
                duration : 7.821,
                type : WinRatioType.BIG_WIN,
                sound: ImmortalFruitsSoundConfig.BIG_WIN,
            },
            {
                ratio : 25,
                text : "Mega Win",
                duration : 7.328,
                type : WinRatioType.MEGA_WIN,
                sound: ImmortalFruitsSoundConfig.MEGA_WIN

            },
            {
                ratio : 50,
                text : "Super Mega Win",
                duration : 7.328,
                type : WinRatioType.SUPER_MEGA_WIN,
                sound: ImmortalFruitsSoundConfig.SUPER_MEGA_WIN
            }
        ]
    };

    PERFORMANCE_LOGGER:boolean = true;
    BETLINES = [4, 2, 6, 8, 1, 10, 9, 7, 3, 5, // left
        12, 18, 20, 16, 11, 14, 15, 17, 13, 19,// left
        21, 22, 23, 24, 25, 26, 27, 28, 29, 30, // left/right
        31, 32, 33, 34, 35, 36, 37, 38, 39, 40, // right
        41, 42, 43, 44, 45, 46, 47, 48, 49, 50,
       // right
    ];
    WIN_BELOW_STAKE: boolean = false; // #305
    private constructor() {
        if(ImmortalFruitsGameConfig.instance) {
            throw new Error("ImmortalFruitsGameConfig.constructor() - Instantiation failed: Singleton.");
        }
    }
}